import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { PackageBooking } from '../../../services/bookingService';
// import QRCode from 'react-qr-code';

interface BookingDetailDialogProps {
  open: boolean;
  onClose: () => void;
  booking: PackageBooking;
  onRedeemBooking: (code: string) => Promise<{success: boolean; booking?: PackageBooking; error?: string}>;
}

const BookingDetailDialog: React.FC<BookingDetailDialogProps> = ({
  open,
  onClose,
  booking,
  onRedeemBooking
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRedeemBooking = async () => {
    if (!booking.redemptionCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await onRedeemBooking(booking.redemptionCode);
      if (!result.success) {
        setError(result.error || 'Failed to redeem booking');
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error redeeming booking:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const bookingDate = booking.bookingDate?.toDate();
  const isRedeemable = booking.status === 'confirmed' && 
                      bookingDate && 
                      new Date(bookingDate) <= new Date();

  return (
     <Dialog
        open={open}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        scroll='paper'
        aria-labelledby='booking-detail-dialog-title'
     >
        <DialogTitle id='booking-detail-dialog-title'>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant='h6'>Booking Details</Typography>
              <IconButton edge='end' color='inherit' onClick={onClose} aria-label='close'>
                 <CloseIcon />
              </IconButton>
           </Box>
        </DialogTitle>

        <DialogContent dividers>
           <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                 <Grid item xs={12} sm={8}>
                    <Typography variant='h5' gutterBottom>
                       {booking.packageName}
                    </Typography>

                    <Typography variant='subtitle1' color='text.secondary' gutterBottom>
                       Booking #{booking.id.slice(0, 8)}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                       <Chip
                          label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          color={
                             booking.status === 'confirmed'
                                ? 'primary'
                                : booking.status === 'completed'
                                  ? 'success'
                                  : booking.status === 'cancelled'
                                    ? 'error'
                                    : 'default'
                          }
                       />
                    </Box>
                 </Grid>

                 <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Paper
                       elevation={3}
                       sx={{
                          p: 2,
                          display: 'inline-block',
                          textAlign: 'center',
                          backgroundColor: '#f5f5f5',
                       }}
                    >
                       <Typography variant='subtitle2' gutterBottom>
                          Redemption Code
                       </Typography>
                       <Typography
                          variant='h6'
                          fontFamily='monospace'
                          fontWeight='bold'
                          color='primary.main'
                       >
                          {booking.redemptionCode}
                       </Typography>
                    </Paper>
                 </Grid>
              </Grid>
           </Box>

           <Divider sx={{ my: 3 }} />

           <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                 <Typography variant='h6' gutterBottom>
                    Booking Information
                 </Typography>

                 <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Date
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>
                          {bookingDate ? bookingDate.toLocaleDateString() : 'N/A'}
                       </Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Time
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>
                          {`${booking.timeSlot.start} - ${booking.timeSlot.end}`}
                       </Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Guests
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>{booking.guests}</Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Created
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>
                          {booking.createdAt ? booking.createdAt.toDate().toLocaleString() : 'N/A'}
                       </Typography>
                    </Grid>

                    {booking.status === 'completed' && booking.redeemedAt && (
                       <>
                          <Grid item xs={4}>
                             <Typography variant='body2' color='text.secondary'>
                                Redeemed
                             </Typography>
                          </Grid>
                          <Grid item xs={8}>
                             <Typography variant='body1'>
                                {booking.redeemedAt.toDate().toLocaleString()}
                             </Typography>
                          </Grid>
                       </>
                    )}
                 </Grid>

                 <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
                    Customer Information
                 </Typography>

                 <Grid container spacing={2}>
                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Name
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>{booking.userName || 'N/A'}</Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Email
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1'>{booking.userEmail || 'N/A'}</Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          User ID
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1' fontFamily='monospace' fontSize='0.875rem'>
                          {booking.userId}
                       </Typography>
                    </Grid>
                 </Grid>

                 <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
                    Payment Information
                 </Typography>

                 <Grid container spacing={2}>
                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Amount
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1' fontWeight='medium'>
                          ${booking.amount.toFixed(2)}
                       </Typography>
                    </Grid>

                    <Grid item xs={4}>
                       <Typography variant='body2' color='text.secondary'>
                          Payment ID
                       </Typography>
                    </Grid>
                    <Grid item xs={8}>
                       <Typography variant='body1' fontFamily='monospace' fontSize='0.875rem'>
                          {booking.paymentId || 'N/A'}
                       </Typography>
                    </Grid>
                 </Grid>

                 {booking.specialRequests && (
                    <>
                       <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
                          Special Requests
                       </Typography>
                       <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                          <Typography variant='body2'>{booking.specialRequests}</Typography>
                       </Paper>
                    </>
                 )}
              </Grid>

              <Grid item xs={12} md={5}>
                 <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant='h6' gutterBottom>
                       Redemption QR Code
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                       {/* <QRCode 
                  value={`https://wetrippen.com/redeem/${booking.venueId}/${booking.redemptionCode}`}
                  size={180}
                /> */}
                    </Box>
                    <Typography variant='body2' color='text.secondary' paragraph>
                       Scan this QR code or enter the redemption code manually to verify the
                       booking.
                    </Typography>

                    {error && (
                       <Typography color='error' variant='body2' sx={{ mt: 1 }}>
                          {error}
                       </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                       {isRedeemable && (
                          <Button
                             variant='contained'
                             color='primary'
                             startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                             onClick={handleRedeemBooking}
                             disabled={loading || booking.status === 'completed'}
                             fullWidth
                             sx={{ mb: 2 }}
                          >
                             {loading ? 'Processing...' : 'Mark as Redeemed'}
                          </Button>
                       )}

                       <Button
                          variant='outlined'
                          startIcon={<PrintIcon />}
                          onClick={handlePrint}
                          fullWidth
                       >
                          Print Details
                       </Button>
                    </Box>
                 </Paper>
              </Grid>
           </Grid>
        </DialogContent>

        <DialogActions>
           <Button onClick={onClose} color='primary'>
              Close
           </Button>
        </DialogActions>
     </Dialog>
  );
};

export default BookingDetailDialog;
